'use client'

import Image, { type ImageProps } from 'next/image'
import {
  type CSSProperties,
  type FC,
  type ReactNode,
  useEffect,
  useState,
} from 'react'

interface UserAvatarProps {
  imageSrc?: ImageProps['src'] | null
  name?: string | null
  alt?: string
  width?: number | string
  height?: number | string
  size?: number | string
  className?: string
  imageClassName?: string
  fallbackClassName?: string
  style?: CSSProperties
  fallbackStyle?: CSSProperties
  unoptimized?: boolean
  sizes?: string
  children?: ReactNode
}

const toCssDimension = (value: number | string) =>
  typeof value === 'number' ? `${value}px` : value

const getNumericSize = (value?: number | string) => {
  if (typeof value === 'number') {
    return value
  }

  if (typeof value === 'string') {
    const parsedValue = Number.parseFloat(value)
    return Number.isFinite(parsedValue) ? parsedValue : null
  }

  return null
}

const getAvatarInitial = (name?: string | null) => {
  const fallbackText = name?.trim() || 'User'
  const matchedCharacter = fallbackText.match(/[A-Za-z0-9]/)

  return matchedCharacter ? matchedCharacter[0].toUpperCase() : 'U'
}

export const UserAvatar: FC<UserAvatarProps> = ({
  imageSrc,
  name,
  alt,
  width,
  height,
  size,
  className,
  imageClassName,
  fallbackClassName,
  style,
  fallbackStyle,
  unoptimized = false,
  sizes,
  children,
}) => {
  const resolvedWidth = width ?? size
  const resolvedHeight = height ?? size
  const hasImage =
    typeof imageSrc === 'string' ? imageSrc.trim().length > 0 : Boolean(imageSrc)
  const [hasImageError, setHasImageError] = useState(false)

  useEffect(() => {
    setHasImageError(false)
  }, [imageSrc])

  const dimensionStyle: CSSProperties = {
    ...style,
  }

  if (resolvedWidth !== undefined) {
    dimensionStyle.width = toCssDimension(resolvedWidth)
  }

  if (resolvedHeight !== undefined) {
    dimensionStyle.height = toCssDimension(resolvedHeight)
  }

  const avatarSize = getNumericSize(resolvedWidth ?? resolvedHeight ?? size)
  const computedFallbackStyle: CSSProperties = {
    fontSize: avatarSize
      ? `${Math.max(14, Math.min(Math.round(avatarSize * 0.42), 42))}px`
      : '18px',
    lineHeight: 1,
    ...fallbackStyle,
  }

  const rootClassName = [
    'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const mediaClassName = ['rounded-full object-cover', imageClassName]
    .filter(Boolean)
    .join(' ')

  const fallbackRootClassName = [
    'flex h-full w-full items-center justify-center rounded-full bg-[#E9E8D7] font-semibold uppercase text-[#040E2E]',
    fallbackClassName,
  ]
    .filter(Boolean)
    .join(' ')

  const imageSizes =
    sizes ??
    (resolvedWidth !== undefined
      ? toCssDimension(resolvedWidth)
      : resolvedHeight !== undefined
        ? toCssDimension(resolvedHeight)
        : '100vw')

  const shouldRenderImage = hasImage && !hasImageError

  return (
    <div className={rootClassName} style={dimensionStyle} role='img' aria-label={alt ?? name ?? 'User avatar'}>
      {shouldRenderImage ? (
        <Image
          src={imageSrc as NonNullable<ImageProps['src']>}
          alt={alt ?? name ?? 'User avatar'}
          fill
          sizes={imageSizes}
          className={mediaClassName}
          unoptimized={unoptimized}
          onError={() => setHasImageError(true)}
        />
      ) : (
        <span className={fallbackRootClassName} style={computedFallbackStyle} aria-hidden='true'>
          {getAvatarInitial(name)}
        </span>
      )}
      {children}
    </div>
  )
}